import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, downloadUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { Search, Plus, Upload, Download, ChevronLeft, ChevronRight, ArrowUpDown, GitMerge } from "lucide-react";

const STATUTS = ["ACTIF", "PROSPECT", "EXPIRE", "SUSPENDU"];
const STATUT_LABELS = { ACTIF: "Actif", PROSPECT: "Prospect", EXPIRE: "Expiré", SUSPENDU: "Suspendu" };
const STATUT_CLS = {
  ACTIF: "bg-cfhtb-success/10 text-cfhtb-success border-cfhtb-success/30",
  PROSPECT: "bg-cfhtb-secondary/10 text-cfhtb-secondary border-cfhtb-secondary/30",
  EXPIRE: "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30",
  SUSPENDU: "bg-cfhtb-error/10 text-cfhtb-error border-cfhtb-error/30",
};
const TYPE_LABELS = { PRATICIEN: "Praticien", VISITEUR: "Visiteur" };

export default function MembersList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [instituts, setInstituts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");
  const [labels, setLabels] = useState(null);

  const q = params.get("q") || "";
  const type = params.get("type") || "";
  const statut = params.get("statut") || "";
  const institut = params.get("institut_id") || "";
  const tag = params.get("tag") || "";
  const sort = params.get("sort") || "created_at";
  const order = params.get("order") || "desc";
  const page = parseInt(params.get("page") || "1", 10);
  const pageSize = parseInt(params.get("page_size") || "200", 10);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (type) qs.set("type", type);
    if (statut) qs.set("statut", statut);
    if (institut) qs.set("institut_id", institut);
    if (tag) qs.set("tag", tag);
    qs.set("sort", sort);
    qs.set("order", order);
    qs.set("page", String(page));
    qs.set("page_size", String(pageSize));
    api(`/admin/membres?${qs}`).then(setData).catch((e) => toast.error(e.message));
  }, [q, type, statut, institut, tag, sort, order, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api("/referentiels/labels").then(setLabels).catch(() => {}); }, []);
  useEffect(() => {
    api("/admin/instituts").then(setInstituts).catch(() => {});
    api("/admin/tags").then(setTags).catch(() => {});
  }, []);

  const toggleSort = (field) => {
    updateParams({ sort: field, order: sort === field && order === "asc" ? "desc" : "asc" });
  };

  const toggleSelect = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const bulkStatus = async (value) => {
    if (selected.length === 0) return;
    try {
      const res = await api("/admin/membres/actions-groupees", {
        method: "POST",
        body: { ids: selected, action: "changer_statut", value },
      });
      toast.success(`${res.modifies} membre(s) mis à jour`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const bulkType = async (value) => {
    if (selected.length === 0) return;
    try {
      const res = await api("/admin/membres/actions-groupees", {
        method: "POST",
        body: { ids: selected, action: "changer_type", value },
      });
      toast.success(`${res.modifies} membre(s) mis à jour`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const exportCsv = (idsOnly) => {
    const qs = new URLSearchParams();
    if (idsOnly && selected.length) qs.set("ids", selected.join(","));
    else {
      if (q) qs.set("q", q);
      if (type) qs.set("type", type);
      if (statut) qs.set("statut", statut);
      if (institut) qs.set("institut_id", institut);
    }
    window.open(downloadUrl(`/admin/membres/export.csv?${qs}`), "_blank");
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const allChecked = data && data.items.length > 0 && data.items.every((m) => selected.includes(m.id));

  const thCls = "text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-3";
  const sortBtn = (field, label) => (
    <button onClick={() => toggleSort(field)} data-testid={`sort-${field}`} className="inline-flex items-center gap-1 hover:text-cfhtb-primary transition-colors">
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sort === field ? "text-cfhtb-primary" : "text-slate-300"}`} />
    </button>
  );

  return (
    <AppLayout variant="admin">
      <div data-testid="members-list-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Membres</h1>
          <div className="flex gap-2">
            <button
              data-testid="admin-doublons-button"
              onClick={() => navigate("/admin/doublons")}
              className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors"
            >
              <GitMerge className="h-4 w-4" /> Doublons
            </button>
            <button
              data-testid="admin-import-csv-button"
              onClick={() => navigate("/admin/membres/import")}
              className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors"
            >
              <Upload className="h-4 w-4" /> Import CSV
            </button>
            <button
              data-testid="admin-new-member-button"
              onClick={() => navigate("/admin/membres/nouveau")}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors"
            >
              <Plus className="h-4 w-4" /> Nouveau membre
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4 flex flex-wrap items-end gap-3">
          <form
            className="flex-1 min-w-[220px]"
            onSubmit={(e) => { e.preventDefault(); updateParams({ q: searchInput }); }}
          >
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="admin-search-members-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Nom, prénom ou email…"
                className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light"
              />
            </div>
          </form>
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Type</label>
            <select data-testid="filter-type-select" value={type} onChange={(e) => updateParams({ type: e.target.value })}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
              <option value="">Tous</option>
              {labels?.types
                ? Object.values(labels.types).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)
                : (<><option value="PRATICIEN">Praticien</option><option value="VISITEUR">Visiteur</option></>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Statut</label>
            <select data-testid="filter-statut-select" value={statut} onChange={(e) => updateParams({ statut: e.target.value })}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light">
              <option value="">Tous</option>
              {STATUTS.map((s) => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Institut</label>
            <select data-testid="filter-institut-select" value={institut} onChange={(e) => updateParams({ institut_id: e.target.value })}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light max-w-[200px]">
              <option value="">Tous</option>
              {instituts.map((i) => <option key={i.id} value={i.id}>{i.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-cfhtb-heading mb-1">Tag</label>
            <select data-testid="filter-tag-select" value={tag} onChange={(e) => updateParams({ tag: e.target.value })}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light max-w-[180px]">
              <option value="">Tous</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
            </select>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="bg-cfhtb-bg border border-cfhtb-light/40 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid="bulk-actions-bar">
            <span className="text-sm font-medium text-cfhtb-heading">{selected.length} sélectionné(s)</span>
            <select
              data-testid="admin-bulk-action-select"
              defaultValue=""
              onChange={(e) => { if (e.target.value) bulkStatus(e.target.value); e.target.value = ""; }}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            >
              <option value="" disabled>Changer le statut…</option>
              {STATUTS.map((s) => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
            </select>
            <select
              data-testid="admin-bulk-type-select"
              defaultValue=""
              onChange={(e) => { if (e.target.value) bulkType(e.target.value); e.target.value = ""; }}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            >
              <option value="" disabled>Changer le type…</option>
              {labels?.types
                ? Object.values(labels.types).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)
                : (<><option value="PRATICIEN">Praticien</option><option value="VISITEUR">Visiteur</option></>)}
            </select>
            <button
              data-testid="bulk-export-csv-button"
              onClick={() => exportCsv(true)}
              className="inline-flex items-center gap-1.5 text-sm text-cfhtb-secondary hover:text-cfhtb-primary font-medium"
            >
              <Download className="h-4 w-4" /> Exporter la sélection
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="members-table">
              <thead className="bg-[#F7F7F7] border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      checked={!!allChecked}
                      onChange={() => {
                        if (!data) return;
                        setSelected(allChecked ? [] : data.items.map((m) => m.id));
                      }}
                      className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary"
                    />
                  </th>
                  <th className={thCls}>{sortBtn("nom", "Nom")}</th>
                  <th className={thCls}>{sortBtn("email", "Email")}</th>
                  <th className={thCls}>Type</th>
                  <th className={thCls}>Statut</th>
                  <th className={thCls}>Institut</th>
                  <th className={thCls}>{sortBtn("date_adhesion", "Adhésion")}</th>
                  <th className={thCls}>{sortBtn("date_naissance", "Anniversaire")}</th>
                </tr>
              </thead>
              <tbody>
                {!data ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-cfhtb-text">Chargement…</td></tr>
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-cfhtb-text" data-testid="members-empty">Aucun membre ne correspond aux critères.</td></tr>
                ) : (
                  data.items.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-cfhtb-bg/50 transition-colors" data-testid={`member-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggleSelect(m.id)}
                          data-testid={`select-member-${m.id}`} className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary" />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Link to={`/admin/membres/${m.id}`} className="font-medium text-cfhtb-heading hover:text-cfhtb-primary transition-colors">
                          {m.nom ? m.nom.toUpperCase() : <span className="italic text-cfhtb-text/60">(sans nom)</span>} {m.prenom}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{m.email_principal}</td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{labels?.types?.[m.type]?.label || TYPE_LABELS[m.type] || m.type}</td>
                      <td className="px-4 py-3">
                        {labels?.statuts?.[m.statut] ? (
                          <span className="text-xs px-2.5 py-0.5 rounded-full font-medium border"
                            style={{ color: labels.statuts[m.statut].couleur, backgroundColor: `${labels.statuts[m.statut].couleur}1a`, borderColor: `${labels.statuts[m.statut].couleur}4d` }}>
                            {labels.statuts[m.statut].label}
                          </span>
                        ) : (
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${STATUT_CLS[m.statut] || ""}`}>
                            {STATUT_LABELS[m.statut] || m.statut}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">{m.institut_nom || "—"}</td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text">
                        {m.date_adhesion ? new Date(m.date_adhesion).toLocaleDateString("fr-FR") : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-cfhtb-text" data-testid={`member-anniversaire-${m.id}`}>
                        {m.date_naissance ? new Date(m.date_naissance).toLocaleDateString("fr-FR") : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-white">
            <p className="text-xs text-cfhtb-text" data-testid="members-total">
              {data ? `${data.total} membre(s) — page ${page} / ${totalPages}` : ""}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select data-testid="page-size-select" value={pageSize}
                onChange={(e) => updateParams({ page_size: e.target.value })}
                className="text-xs border border-slate-300 rounded-md px-2 py-1.5 text-cfhtb-text bg-white">
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <button data-testid="export-csv-button" onClick={() => exportCsv(false)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 hover:bg-cfhtb-bg transition-colors">
                <Download className="h-4 w-4" /> Exporter (CSV)
              </button>
              <button data-testid="pagination-prev" disabled={page <= 1}
                onClick={() => updateParams({ page: String(page - 1) })}
                className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <select data-testid="page-select" value={page}
                onChange={(e) => updateParams({ page: e.target.value })}
                className="text-xs border border-slate-300 rounded-md px-2 py-1.5 text-cfhtb-text bg-white">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>Page {n}</option>
                ))}
              </select>
              <button data-testid="pagination-next" disabled={page >= totalPages}
                onClick={() => updateParams({ page: String(page + 1) })}
                className="p-1.5 rounded-md border border-slate-300 text-cfhtb-text hover:bg-slate-50 disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
